import { Drawer, useToast } from '@teamsnap/snap-ui';
import { AMPLITUDE_SCREEN_NAMES, useAmplitude } from 'hooks/amplitude';
import { FormPage } from 'hooks/postRegEditDrawer';
import * as React from 'react';
import { useState } from 'react';
import {
  PostRegEditDrawerProvider,
  usePostRegEditActions,
  usePostRegEditDrawer,
} from '../../../../hooks/postRegEditDrawer/usePostRegEditDrawer';
import { FormResult } from '../types';
import CloseWarning from './CloseWarning';
import ParticipantInfo from './Edit/ParticipantInfo';
import PostRegEditForm from './Edit/PostRegEditForm';
import { EditConfirmationModal } from './EditConfirmationModal';
import PostRegEditDrawerFooterActions from './PostRegEditDrawerFooterActions';
import DrawerTitle from './PostRegEditDrawerTitle';
import PostRegEditsReview from './Review/PostRegEditsReview';

export interface Props {
  formResult: FormResult;
  isPostRegEditDrawerOpen: boolean;
  setIsPostRegEditDrawerOpen: (boolean) => void;
  refetch: () => void;
}

const EditDrawer: React.FunctionComponent<Props> = ({
  formResult,
  isPostRegEditDrawerOpen,
  setIsPostRegEditDrawerOpen,
  refetch,
}) => {
  const [isCloseWarningOpen, setIsCloseWarningOpen] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const { formPage, dirtyFields, loading } = usePostRegEditDrawer(
    isPostRegEditDrawerOpen && +formResult?.formResultId,
    isPostRegEditDrawerOpen && formResult?.orderId
  );
  const { closeDrawer } = usePostRegEditActions();
  const { amplitudeTrack } = useAmplitude();
  const { toast, dismiss } = useToast();

  React.useEffect(() => {
    if (loading.error) {
      toast({
        variant: 'negative',
        title: loading.error,
      });
    }
    return () => {
      dismiss();
    };
  }, [loading?.error]);

  const onCancel = () => {
    const hasEdits = Object.keys(dirtyFields).length > 0;
    hasEdits ? setIsCloseWarningOpen(true) : onConfirmCancel();
  };

  const onConfirmCancel = () => {
    setIsPostRegEditDrawerOpen(false);
    setIsCloseWarningOpen(false);
    closeDrawer();
  };

  React.useEffect(
    () => {
      if (!isPostRegEditDrawerOpen) {
        return;
      }
      const screenName = {
        [FormPage.Editing]: AMPLITUDE_SCREEN_NAMES.PRE_EDIT,
        [FormPage.Reviewing]: AMPLITUDE_SCREEN_NAMES.PRE_REVIEW,
      }[formPage];

      screenName && amplitudeTrack.surfaceViewed(screenName);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isPostRegEditDrawerOpen, formPage]
  );

  return (
    <>
      <Drawer
        title="View and Edit Registration"
        open={isPostRegEditDrawerOpen}
        onCloseClick={onCancel}
        onClickOutside={isConfirmationOpen ? () => {} : onCancel}
        // https://github.com/teamsnap/snap-ui/issues/807 re-enable after fixed
        // allowEscapeKey
        size="large"
        showOverlay
        testId="PostRegEditDrawer--drawer"
        className="sui-p-0"
      >
        <Drawer.Header data-testid="PostRegEditDrawer--header">
          <DrawerTitle />
        </Drawer.Header>
        <Drawer.Content
          data-testid="PostRegEditDrawer--content"
          className="sui-body"
        >
          {formPage === FormPage.Editing ? (
            <>
              <ParticipantInfo />
              <PostRegEditForm
                key={isPostRegEditDrawerOpen ? 'open' : 'closed'}
              />
            </>
          ) : (
            <PostRegEditsReview />
          )}
        </Drawer.Content>
        <Drawer.Footer data-testid="PostRegEditDrawer--footer">
          <PostRegEditDrawerFooterActions
            openConfirmation={() => {
              setIsPostRegEditDrawerOpen(false);
              setTimeout(() => {
                // Wait for the drawer to close before opening confirmation modal
                // Otherwise the drawer freezes and doesn't complete closing animation
                setIsConfirmationOpen(true);
              }, 100);
            }}
            closeDrawer={onCancel}
          />
        </Drawer.Footer>
      </Drawer>
      <CloseWarning
        isOpen={!isConfirmationOpen && isCloseWarningOpen}
        onClose={() => setIsCloseWarningOpen(false)}
        onConfirm={onConfirmCancel}
      />
      <EditConfirmationModal
        isOpen={isConfirmationOpen}
        formResult={formResult}
        onClose={() => {
          setIsConfirmationOpen(false);
          onConfirmCancel();
          refetch();
        }}
      />
    </>
  );
};

const PostRegEditDrawer = React.memo(
  ({
    formResult,
    isPostRegEditDrawerOpen,
    setIsPostRegEditDrawerOpen,
    refetch,
  }: Props) => (
    <PostRegEditDrawerProvider>
      <EditDrawer
        formResult={formResult}
        isPostRegEditDrawerOpen={isPostRegEditDrawerOpen}
        setIsPostRegEditDrawerOpen={setIsPostRegEditDrawerOpen}
        refetch={refetch}
      />
    </PostRegEditDrawerProvider>
  )
);

export default PostRegEditDrawer;
